import { useQuery } from "@tanstack/react-query";
import DughnutComponent from "components/charts/doughnut.component";
import React from "react";
import { getDeeperDiveSideData } from "services/deeper-dive.service";

type DeepDiveSideDataProps = {
  showTopThree?: boolean;
  showCareerAwareness?: boolean;
  externalData?: {
    videoProgressPercentage: number;
    previewCenterData: string;
    deeperDivePercentage: number;
    deeperdiveCenterData: string;
  };
}

const DeepDiveSideData: React.FC<DeepDiveSideDataProps> = ({
  showTopThree = true,
  showCareerAwareness = true,
  externalData,
}) => {
  const { data: deeperDiveData } = useQuery({
    queryKey: ["deeperDiveData"],
    queryFn: () => getDeeperDiveSideData(),
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: !externalData,
  });

  const videoProgressPercentage = externalData
    ? externalData.videoProgressPercentage
    : deeperDiveData?.preview_progress.preview_completed_percentage ?? 0;

  const previewCenterData = externalData
    ? externalData.previewCenterData
    : `${deeperDiveData?.preview_progress.total_previewed_count}/${deeperDiveData?.preview_progress.total_unlocked_career_count}`;

  const deeperDivePercentage = externalData
    ? externalData.deeperDivePercentage
    : deeperDiveData?.deep_dive_progress?.deep_dive_progress_percent ?? 0;

  const deeperdiveCenterData = externalData
    ? externalData.deeperdiveCenterData
    : `${deeperDiveData?.deep_dive_progress.completed_deep_dive_career_count}/${deeperDiveData?.deep_dive_progress.total_deep_dive_career_count}`;

  const chartDataPreviewProgress = {
    datasets: [
      {
        data: [videoProgressPercentage, 100 - videoProgressPercentage],
        backgroundColor: ["#5EA3E0", "#E8E8E8"],
      },
    ],
  };

  const chartDataDeeperDiveProgress = {
    datasets: [
      {
        data: [deeperDivePercentage, 100 - deeperDivePercentage],
        backgroundColor: ["#5EA3E0", "#E8E8E8"],
      },
    ],
  };

  const hasData = externalData || deeperDiveData;

  return (
    <>
      {hasData && (
        <div className="flex flex-row gap-5 overflow-auto lg:flex-col lg:h-auto">
          <div className="w-full rounded-lg border border-border bg-white px-4 py-3 flex flex-col items-center shrink-0">
            <span className="text-lg font-semibold leading-6 text-brand text-center">Career Video Progress</span>
            <div className="flex-1 flex items-center justify-center py-2">
              <div className="w-[140px] h-[140px]">
                <DughnutComponent
                  centerPercentage={true}
                  ratioData={previewCenterData}
                  centerValue={`${Math.round(videoProgressPercentage)}%`}
                  data={chartDataPreviewProgress}
                  fontStyle="700 18px 'Nunito', sans-serif"
                />
              </div>
            </div>
          </div>
          <div className="w-full rounded-lg border border-border bg-white px-4 py-3 flex flex-col items-center shrink-0">
            <span className="text-lg font-semibold leading-6 text-brand text-center">Deeper Dive Progress</span>
            <div className="flex-1 flex items-center justify-center py-2">
              <div className="w-[140px] h-[140px]">
                <DughnutComponent
                  centerPercentage={true}
                  ratioData={deeperdiveCenterData}
                  centerValue={`${Math.round(deeperDivePercentage)}%`}
                  data={chartDataDeeperDiveProgress}
                  fontStyle="700 18px 'Nunito', sans-serif"
                />
              </div>
            </div>
          </div>
          {showTopThree && deeperDiveData && (
            <div className="w-full rounded-lg border border-border bg-white px-3 py-3 flex flex-col items-center shrink-0">
              <span className="text-lg font-semibold leading-6 text-brand text-center">Top 3</span>
              <div className="flex-1 flex items-start justify-between w-full py-2 gap-1">
                {deeperDiveData.top_three_careers.map((item: any) => (
                  <div key={item.id} className="flex flex-col items-center gap-1.5 text-center flex-1 basis-0 min-w-0">
                    <img
                      className="w-[48px] h-[48px] rounded-full object-cover border border-border shrink-0 shadow-md"
                      src={item.icon_url}
                      alt={item.name}
                      title={item.name}
                    />
                    <span className="text-[12px] font-normal leading-4 text-text-heading text-center w-full px-0.5 break-words">
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {showCareerAwareness && deeperDiveData && (
            <div className="w-full rounded-lg border border-border bg-white px-4 py-3 flex flex-col items-center shrink-0">
              <span className="text-lg font-semibold leading-6 text-brand text-center">My Career Awareness</span>
              <div className="flex-1 flex items-center justify-center w-full py-2">
                {deeperDiveData.my_career_ig_level ? (
                  <img
                    key={deeperDiveData.my_career_ig_level.slug}
                    src={`/assets/svg/badge-${deeperDiveData.my_career_ig_level.slug}.svg`}
                    className="w-[48px] h-auto"
                    alt={deeperDiveData.my_career_ig_level?.title}
                    title={deeperDiveData.my_career_ig_level?.title}
                  />
                ) : (
                  <span className="text-sm font-normal leading-5 text-text-muted">No earned badges yet</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DeepDiveSideData;
